// React
import { useContext, useEffect, useRef, useState } from "react";

// @mui material components
import { Card, Collapse, Icon, Menu, MenuItem, Skeleton, Tab, Tabs, Tooltip } from "@mui/material";

import { gql, useMutation, useQuery, useSubscription } from "@apollo/client";
import Footer from "components/Footer";
import DashboardLayout from "components/LayoutContainers/DashboardLayout";
import DashboardNavbar from "components/Navbars/DashboardNavbar";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import { MessageManager } from "context";
import { UserContext } from "context/user";
import { getUproColor } from "utils/colors";
import Areas from "./components/areas";
import Mesas from "./components/mesas";
import ModalNuevoPedido from "./components/ModalNuevoPedido";
import PedidosList from "./components/pedidos";
import ModalConsumoInterno from "./components/ModalConsumoInterno";
import { MenuContext } from "context/menuContext";
import ModalReporteConsumoInterno from "./components/ModalReporteConsumoInterno";
import ModalExpedientesSucursal from "./components/ModalExpedientesSucursal";
import { PERMISOS_COMPRAS } from "layouts/compras/permisos";

const NUEVO_PEDIDO_SUBSCRIPTION = gql`
	subscription NuevoPedidoModuloPedidos {
		nuevoPedido {
			id
			tipo
		}
	}
`;

function Pedidos() {
	const [selectedSucursal, setSelectedSucursal] = useState(null);
	const [openModalNuevoPedido, setOpenModalNuevoPedido] = useState(false);
	const [anchorEl, setAnchorEl] = useState(null);
	const [openSucursalMenu, setOpenSucursalMenu] = useState(false);
	const [refetchPedidos, setRefetchPedidos] = useState(null);
	const { user, verificarAcceso } = useContext(UserContext);
	const { handleSnackbar } = useContext(MessageManager);
	const isMozo = user?.tipoUser == "Mozo";
	const ultimoPedidoNotificadoRef = useRef(null);
	const { data: nuevoPedidoEvent } = useSubscription(NUEVO_PEDIDO_SUBSCRIPTION);
	const [selectedTab, setSelectedTab] = useState(isMozo ? "Mesas" : "Pedidos");
	const [modalConusmoInterno, setModalConsumoInterno] = useState(false);
	const [modalReporteConsumo, setModalReporteConsumoInterno] = useState(false);
	const { menu } = useContext(MenuContext);
	const [openExpedientes, setOpenExpedientes] = useState(false);

	useEffect(() => {
		const pedido = nuevoPedidoEvent?.nuevoPedido;
		const puedeRecibirNotificacion =
			user?.username?.trim().toLowerCase() === "caja" || isMozo;

		if (
			!pedido?.id ||
			pedido.tipo !== "Cafetería" ||
			!puedeRecibirNotificacion ||
			ultimoPedidoNotificadoRef.current === String(pedido.id)
		) {
			return;
		}

		ultimoPedidoNotificadoRef.current = String(pedido.id);
		handleSnackbar(`Se ha registrado un nuevo pedido #${pedido.id}`, "info");
	}, [handleSnackbar, isMozo, nuevoPedidoEvent, user?.username]);

	useEffect(() => {
		if (isMozo && selectedTab === "Pedidos") {
			setSelectedTab("Mesas");
		}
	}, [isMozo, selectedTab]);

	const { data: dataSucurales, loading: loadingSucursales } = useQuery(
		gql`
      query getSucursales($filter: JSON) {
        sucursales(filter: $filter) {
          id
          nombre
          sede {
            id
            nombre
          }
          administraStock
          principal
        }
      }
    `,
		{
			variables: {
				filter: {
					administraStock: true,
					id: user?.sucursales ? user?.sucursales.map((sucursal) => sucursal.id) : undefined,
				},
			},
			onCompleted: (data) => {
				if (data?.sucursales?.length > 0) {
					setSelectedSucursal(data?.sucursales[0].id);
				}
			},
		}
	);

	const [finalizarPedido] = useMutation(
		gql`
      mutation finalizarPedido($id: ID!, $bandejas: Int, $observacion: String) {
        finalizarPedido(id: $id, bandejas: $bandejas, observacion: $observacion) {
          id
        }
      }
    `
	);

	const handleCloseSucursalMenu = () => {
		setAnchorEl(null);
		setOpenSucursalMenu(false);
	};

	const handleOpenSucursalMenu = (event) => {
		setAnchorEl(event.currentTarget);
		setOpenSucursalMenu(true);
	};

	const renderSucursalMenu = (
		<Menu
			id="simple-menu"
			anchorEl={anchorEl}
			open={Boolean(openSucursalMenu)}
			onClose={handleCloseSucursalMenu}
			transformOrigin={{ horizontal: "left", vertical: "top" }}
			anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
			PaperProps={{
				elevation: 0,
				sx: {
					overflow: "visible",
					mt: 1.5,
					"& .MuiAvatar-root": {
						width: 32,
						height: 32,
						ml: -0.5,
						mr: 1,
					},
				},
			}}
		>
			{dataSucurales?.sucursales?.map((sucursal, index) => (
				<MenuItem
					key={index}
					onClick={() => {
						setSelectedSucursal(sucursal.id);
						handleCloseSucursalMenu();
					}}
				>
					<SoftBox
						display="flex"
						justifyContent="space-between"
						alignItems="center"
						sx={{
							width: "100%",
						}}
					>
						<SoftBox mr={2}>
							<SoftTypography variant="p" fontWeight="medium">
								{sucursal.sede.nombre}
							</SoftTypography>
							<SoftTypography variant="p">{` - ${sucursal.nombre}`}</SoftTypography>
						</SoftBox>
						<SoftBox>
							<Icon
								fontSize="small"
								sx={{
									verticalAlign: "middle",
									color:
										sucursal.id === selectedSucursal
											? getUproColor("uproPink")
											: getUproColor("uproGreen"),
								}}
							>
								location_on
							</Icon>
						</SoftBox>
					</SoftBox>
				</MenuItem>
			))}
		</Menu>
	);

	//FUNCION PARA LECTOR DE QR
	const [scannedCode, setScannedCode] = useState("");
	const [lastInputTime, setLastInputTime] = useState(null);
	const [buffer, setBuffer] = useState("");

	useEffect(() => {
		const handleKeyPress = (e) => {
			const currentTime = new Date().getTime();
			if (lastInputTime && currentTime - lastInputTime > 100) {
				// Si pasa más de 100ms entre teclas, asumimos que es humano
				setBuffer("");
			}

			setLastInputTime(currentTime);

			if (e.key === "Enter") {
				setScannedCode(buffer);
				setBuffer("");
			} else {
				setBuffer((prev) => prev + e.key);
			}
		};

		window.addEventListener("keydown", handleKeyPress);

		return () => {
			window.removeEventListener("keydown", handleKeyPress);
		};
	}, [lastInputTime, buffer]);

	useEffect(() => {
		if (scannedCode) {
			finalizarPedido({
				variables: {
					id: scannedCode,
					bandejas: 1,
					observacion: "Pedido marcado como entregado por lectora QR",
				},
			})
				.then(() => {
					setScannedCode("");
					handleSnackbar("Pedido entregado", "success");
				})
				.catch((error) => {
					console.log(error?.message);
					handleSnackbar(error?.message, "error");
				});
		}
	}, [scannedCode, finalizarPedido]);

	return (
		<DashboardLayout>
			<DashboardNavbar />

			<SoftBox py={3}>
				<SoftBox mb={3}>
					<Card>
						<SoftBox
							display={{ xs: "none", lg: "flex" }}
							justifyContent="space-between"
							alignItems="center"
							p={3}
						>
							<SoftBox pb={{ xs: 3, sm: 0 }} display="flex" justifyContent="space-between">
								<SoftBox
									display="flex"
									alignItems="center"
									onClick={handleOpenSucursalMenu}
									mr={{
										xs: 2,
										sm: 0,
									}}
								>
									<Icon color="primary">location_on</Icon>
									<SoftTypography
										variant="h6"
										sx={{
											cursor: "pointer",
										}}
										ml={1}
									>
										{loadingSucursales ? (
											<Skeleton width={100} />
										) : dataSucurales?.sucursales?.find(
											(sucursal) => sucursal.id === selectedSucursal
										) ? (
											`${dataSucurales?.sucursales?.find(
												(sucursal) => sucursal.id === selectedSucursal
											)?.sede.nombre
												} - ${dataSucurales?.sucursales?.find(
													(sucursal) => sucursal.id === selectedSucursal
												)?.nombre
												}`.length > 30 ? (
												`${dataSucurales?.sucursales?.find(
													(sucursal) => sucursal.id === selectedSucursal
												)?.sede.nombre
													} - ${dataSucurales?.sucursales?.find(
														(sucursal) => sucursal.id === selectedSucursal
													)?.nombre
													}`.substring(0, 25) + "..."
											) : (
												`${dataSucurales?.sucursales?.find(
													(sucursal) => sucursal.id === selectedSucursal
												)?.sede.nombre
												} - ${dataSucurales?.sucursales?.find(
													(sucursal) => sucursal.id === selectedSucursal
												)?.nombre
												}`
											)
										) : (
											"No está asignado a ningún pañol"
										)}
									</SoftTypography>
									<Icon color="dark">{openSucursalMenu ? "arrow_drop_up" : "arrow_drop_down"}</Icon>
								</SoftBox>
								<SoftBox
									display={{
										xs: "flex",
										sm: "none",
									}}
									justifyContent="flex-end"
								>
									<SoftBox>
										<Tooltip title="Opciones" placement="top">
											<SoftButton color="primary" circular iconOnly>
												<Icon sx={{ fontWeight: "light" }}>more_vert</Icon>
											</SoftButton>
										</Tooltip>
									</SoftBox>
								</SoftBox>
							</SoftBox>
							<SoftBox pb={{ xs: 3, sm: 0 }} display="flex" gap={2} justifyContent="flex-end">
								{/* {verificarAcceso(14) && <SoftBox
									display={{ xs: "flex-row", sm: "flex" }}
									justifyContent="end"
									alignItems="flex-end"
								>
									<SoftBox
										display={{
											xs: "none",
											sm: "block",
										}}
									>
										<Tooltip title="Consumo destinado al personal" placement="top">
											<SoftButton
												color="info"
												circular
												onClick={(event) => {
													menu({
														open: event.currentTarget,
														align: "right",
														options: [
															{
																name: "Cargar consumo del personal",
																icon: { icon: "people_alt", color: getUproColor("uproGreen") },
																onClick: () => {
																	setModalConsumoInterno(true)
																},
															},
															{
																name: "Reporte de consumo interno",
																icon: { icon: "restaurant", color: getUproColor("uproBlue") },
																onClick: () => {
																	setModalReporteConsumoInterno(true);
																},
															},
														],
														handleClose: () => { },
													})
												}
												}
											>
												<Icon>person</Icon>
												&nbsp;Consumo interno
											</SoftButton>
										</Tooltip>
									</SoftBox>
								</SoftBox>} */}
								{verificarAcceso(PERMISOS_COMPRAS.SOLICITAR_DESDE_PEDIDOS) && <SoftBox
									display={{ xs: "flex-row", sm: "flex" }}
									justifyContent="end"
									alignItems="flex-end"
								>
									<SoftBox
										display={{
											xs: "none",
											sm: "block",
										}}
									>
										<Tooltip title="Hacer una solicitud de compra" placement="top">
											<SoftButton
												color="success"
												circular
												onClick={(event) => setOpenExpedientes(true)}
											>
												<Icon>shopping_cart</Icon>
												&nbsp;Solicitud de Compra
											</SoftButton>
										</Tooltip>
									</SoftBox>
								</SoftBox>}
							</SoftBox>
						</SoftBox>
						<SoftBox p={3} pt={0}>
							<Tabs
								value={selectedTab}
								onChange={(e, value) => {
									setSelectedTab(value);
								}}
							>
								{!isMozo && (
									<Tab
										label={
											<SoftBox display="flex" alignItems="center">
												<SoftBox display="flex" alignItems="center">
													<Icon color={selectedTab === "Pedidos" ? "primary" : "secondary"}>
														receipt
													</Icon>
												</SoftBox>
												<SoftTypography
													variant="h6"
													fontWeight={selectedTab === "Pedidos" ? "bold" : "regular"}
												>
													Pedidos
												</SoftTypography>
											</SoftBox>
										}
										value={"Pedidos"}
									/>
								)}
								<Tab
									label={
										<SoftBox display="flex" alignItems="center">
											<SoftBox display="flex" alignItems="center">
												<Icon color={selectedTab === "Mesas" ? "primary" : "secondary"}>
													table_restaurant
												</Icon>
											</SoftBox>
											<SoftTypography
												variant="h6"
												fontWeight={selectedTab === "Mesas" ? "bold" : "regular"}
											>
												Mesas
											</SoftTypography>
										</SoftBox>
									}
									value={"Mesas"}
								/>
								<Tab
									label={
										<SoftBox display="flex" alignItems="center">
											<SoftBox display="flex" alignItems="center">
												<Icon color={selectedTab === "Áreas" ? "primary" : "secondary"}>
													zoom_out_map
												</Icon>
											</SoftBox>
											<SoftTypography
												variant="h6"
												fontWeight={selectedTab === "Áreas" ? "bold" : "regular"}
											>
												Áreas
											</SoftTypography>
										</SoftBox>
									}
									value={"Áreas"}
								/>
							</Tabs>
						</SoftBox>
						<SoftBox p={3} pt={0}>
							<Collapse in={selectedTab === "Pedidos"} unmountOnExit>
								<PedidosList setRefetchPedidos={setRefetchPedidos} />
							</Collapse>
							<Collapse in={selectedTab === "Mesas"} unmountOnExit>
								<Mesas />
							</Collapse>
							<Collapse in={selectedTab === "Áreas"} unmountOnExit>
								<Areas />
							</Collapse>
						</SoftBox>
					</Card>
					{renderSucursalMenu}
					<ModalNuevoPedido
						open={openModalNuevoPedido}
						handleClose={() => setOpenModalNuevoPedido(false)}
						refetch={() => {
							if (refetchPedidos) refetchPedidos();
						}}
					/>
					<ModalConsumoInterno open={modalConusmoInterno} onClose={() => setModalConsumoInterno(false)} />
					<ModalReporteConsumoInterno open={modalReporteConsumo} handleClose={() => setModalReporteConsumoInterno(false)} />
					<ModalExpedientesSucursal open={openExpedientes} handleClose={() => setOpenExpedientes(false)} idSucursal={selectedSucursal} />
				</SoftBox>
			</SoftBox>
			<SoftBox
				sx={{
					display: {
						xs: "none",
						xl: "block",
					},
				}}
			>
				<Footer />
			</SoftBox>
		</DashboardLayout>
	);
}

export default Pedidos;
